/**
 * Invite Candidates Modal Component
 * Allows org users to send simulation invitations to external candidates via email
 */

import React, { useState } from 'react';
import { X, Mail, Send, AlertCircle, CheckCircle, Loader2, Clock, Target, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface Simulation {
    id: string;
    title: string;
    description: string;
    difficulty: string;
    duration: string;
    role: string;
}

interface InviteCandidatesModalProps {
    simulation: Simulation;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (count: number) => void;
}

interface InviteResult {
    successful: string[];
    failed: { email: string; error: string }[];
    invalid: string[];
}

export function InviteCandidatesModal({
    simulation,
    isOpen,
    onClose,
    onSuccess
}: InviteCandidatesModalProps) {
    const [emailInput, setEmailInput] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<InviteResult | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    const parseEmails = (input: string): string[] => {
        return input
            .split(/[,\n;]/)
            .map(email => email.trim().toLowerCase())
            .filter(email => email.length > 0);
    };

    const validateEmail = (email: string): boolean => {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
    };

    const emails = parseEmails(emailInput);
    const validEmails = emails.filter(validateEmail);
    const invalidEmails = emails.filter(e => !validateEmail(e));

    const handleSendInvites = async () => {
        if (validEmails.length === 0) {
            setError('Please enter at least one valid email address');
            return;
        }

        setSending(true);
        setError(null);
        setResult(null);

        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.post(
                `${API_BASE}/simulations/${simulation.id}/invite`,
                {
                    emails: validEmails,
                    simulation_title: simulation.title,
                    simulation_description: simulation.description,
                    simulation_role: simulation.role,
                    simulation_difficulty: simulation.difficulty,
                    simulation_duration: simulation.duration
                },
                { headers: authHeaders }
            );

            const data = response.data.data;
            setResult(data.results);

            if (data.invited > 0) {
                setShowSuccess(true);
                if (onSuccess) {
                    onSuccess(data.invited);
                }
            }
        } catch (err: any) {
            console.error('Failed to send invitations:', err);
            setError(err.response?.data?.message || 'Failed to send invitations');
        } finally {
            setSending(false);
        }
    };

    const handleClose = () => {
        setEmailInput('');
        setError(null);
        setResult(null);
        setShowSuccess(false);
        onClose();
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'Easy': return 'bg-green-100 text-green-700';
            case 'Medium': return 'bg-yellow-100 text-yellow-700';
            case 'Hard': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={handleClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: 'spring', duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <Users className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Invite Candidates</h2>
                                    <p className="text-emerald-100 text-sm">Send simulation assessment invitations</p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-5">
                        {/* Simulation Preview */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900 mb-1">{simulation.title}</h3>
                                    <p className="text-sm text-emerald-600 mb-2">Role: {simulation.role}</p>
                                    <p className="text-sm text-slate-600 line-clamp-2">{simulation.description}</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className={clsx('px-2 py-1 rounded text-xs font-medium', getDifficultyColor(simulation.difficulty))}>
                                        {simulation.difficulty}
                                    </span>
                                    <span className="flex items-center gap-1 text-xs text-slate-500">
                                        <Clock className="w-3 h-3" />
                                        {simulation.duration}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {showSuccess ? (
                            /* Success State */
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center py-6"
                            >
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">Invitations Sent!</h3>
                                <p className="text-slate-600 mb-4">
                                    {result?.successful.length || 0} invitation(s) sent successfully.
                                </p>
                                
                                {result?.successful && result.successful.length > 0 && (
                                    <div className="bg-emerald-50 rounded-lg p-3 text-left mb-4">
                                        <p className="text-sm font-medium text-emerald-800 mb-2">Sent to:</p>
                                        <div className="flex flex-wrap gap-1">
                                            {result.successful.map(email => (
                                                <span key={email} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                                                    {email}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {result?.failed && result.failed.length > 0 && (
                                    <div className="bg-red-50 rounded-lg p-3 text-left mb-4">
                                        <p className="text-sm font-medium text-red-800 mb-2">Failed to send:</p>
                                        <div className="space-y-1">
                                            {result.failed.map(({ email, error }) => (
                                                <p key={email} className="text-xs text-red-600">
                                                    {email}: {error}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                                >
                                    Done
                                </button>
                            </motion.div>
                        ) : (
                            /* Input State */
                            <>
                                {/* Email Input */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        <Mail className="w-4 h-4 inline mr-1" />
                                        Candidate Email Addresses
                                    </label>
                                    <textarea
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        placeholder="Enter email addresses (one per line, or comma-separated)&#10;e.g. john@company.com, jane@company.com"
                                        rows={4}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm"
                                        disabled={sending}
                                    />
                                    <p className="text-xs text-slate-500 mt-2">
                                        Separate multiple emails with commas, semicolons, or new lines
                                    </p>
                                </div>

                                {/* Email Preview */}
                                {emails.length > 0 && (
                                    <div className="space-y-2">
                                        {validEmails.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {validEmails.map(email => (
                                                    <span
                                                        key={email}
                                                        className="inline-flex items-center px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs"
                                                    >
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                        {email}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {invalidEmails.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {invalidEmails.map(email => (
                                                    <span
                                                        key={email}
                                                        className="inline-flex items-center px-2 py-1 bg-red-50 text-red-600 rounded text-xs"
                                                    >
                                                        <AlertCircle className="w-3 h-3 mr-1" />
                                                        {email}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Error Display */}
                                {error && (
                                    <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                        <p className="text-sm">{error}</p>
                                    </div>
                                )}

                                {/* Info Box */}
                                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                    <div className="flex items-start gap-3">
                                        <Target className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">What happens next?</p>
                                            <ul className="space-y-1 text-blue-700">
                                                <li>Candidates receive an email invitation</li>
                                                <li>They create an account to access the simulation</li>
                                                <li>Their progress appears in your Candidates dashboard</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between pt-2">
                                    <button
                                        onClick={handleClose}
                                        disabled={sending}
                                        className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSendInvites}
                                        disabled={sending || validEmails.length === 0}
                                        className={clsx(
                                            'flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all duration-200',
                                            validEmails.length > 0 && !sending
                                                ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/25'
                                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        )}
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                Send {validEmails.length > 0 && `(${validEmails.length})`} Invitation{validEmails.length !== 1 ? 's' : ''}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default InviteCandidatesModal;

